import { CgSpinner } from 'react-icons/cg';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar } from '@components/Avatar/Avatar';
import { MODEL_VERSIONS } from '@/_data/constants';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import { api } from '@/trpc/react';
import { Model } from '@acme/db';
import { useMemo } from 'react';

interface ModelSelectorProps {
  selectedModel: {
    status: string;
    id: string;
    name: string;
    trainingMeta: PrismaJson.TrainingMeta | null;
    readableName: string | null;
    selfieUrl: string | null;
  };
  modelRepository: {
    name?: string;
    id?: string;
  } | null;
  onModelChange: (model: {
    model: Omit<Model, 'trainingData' | 'trainer' | 'modelVersion' | 'parentId' | 'isLatest' | 'deprecatedName'>;
    modelRepository: {
      id: string;
      name: string;
    };
  }) => void;
  className?: string;
  teamId?: string;
}

export function ModelSelector({
  selectedModel,
  onModelChange,
  className,
  modelRepository,
  teamId
}: ModelSelectorProps) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = api.model.getUserModels.useInfiniteQuery(
    { includeTrainingMeta: true, teamId },
    { getNextPageParam: lastPage => lastPage.nextCursor }
  );

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    rootMargin: '0px 0px 400px 0px'
  });

  const models = useMemo(() => {
    return data?.pages.flatMap(page => page.models) || [];
  }, [data?.pages]);

  const relatedTeam = useMemo(() => {
    return models.find(model => model.teams?.some(team => team.id === teamId))?.teams?.find(team => team.id === teamId);
  }, [models, teamId]);

  const allTeams = useMemo(() => {
    return models.flatMap(model => model.teams);
  }, [models]);

  const model = useMemo(() => {
    return {
      allTeams,
      relatedTeam
    };
  }, [allTeams, relatedTeam]);

  if (isPending) {
    return (
      <div className="flex h-16 items-center gap-2 px-3 rounded-md bg-muted border border-foreground/20 w-full min-w-64 justify-start ">
        <div className="flex items-center gap-2 w-full justify-start">
          <div className="h-8 w-8 rounded-full bg-foreground/10 animate-pulse" />
          <div className="flex flex-col gap-0 items-start">
            <div className="h-4 w-32 bg-foreground/10  animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Select
      value={selectedModel?.id ?? ''}
      onValueChange={value => {
        const model = models.find(m => m.id === value);
        if (model) {
          onModelChange({
            model: model,
            modelRepository: {
              id: model.modelRepositoryId,
              name: model.modelRepositoryName
            }
          });
        }
      }}
    >
      <SelectTrigger
        className={`flex h-16 items-center gap-2 px-3 rounded-md bg-muted border border-foreground/20 w-full md:w-fit min-w-64 justify-start ${
          className ?? ''
        }`}
      >
        <div className="flex items-center gap-2 w-full justify-start">
          <Avatar image={selectedModel?.selfieUrl!} className="h-8 w-8 shrink-0" name={selectedModel?.name} />
          <div className="flex flex-col gap-0 items-start">
            <span className="text-xs text-muted-foreground">Model selected</span>
            <SelectValue placeholder="No model selected" className="text-base p-0">
              {modelRepository?.name ?? 'Select a model...'}
              {teamId && <span className="text-xs text-muted-foreground ml-1">{model.relatedTeam?.name}</span>}
            </SelectValue>
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="max-w-sm max-h-[300px]">
        {models.map(model => (
          <SelectItem key={model.id} value={model.id} disabled={model.version !== MODEL_VERSIONS['flux.1-dev']}>
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-2 w-full">
                <Avatar image={model.avatarUrl!} className="h-8 w-8 shrink-0" name={model.name} />
                <div className="flex flex-col gap-0 min-w-0">
                  <div>
                    {model.modelRepositoryName}
                    {model.version !== MODEL_VERSIONS['flux.1-dev'] && (
                      <span className="ml-2 text-xs text-muted-foreground">(Outdated)</span>
                    )}
                  </div>
                  <div className="text-xs opacity-50 truncate break-all text-wrap line-clamp-1">
                    {teamId ? (
                      <span>
                        {relatedTeam?.name} {model.teams?.length > 1 && `and ${model.teams.length - 1} more`}
                      </span>
                    ) : (
                      <span>
                        {model.teams && model.teams.length > 0 && model.teams.map(team => team.name).join(' • ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SelectItem>
        ))}
        {hasNextPage && (
          <div ref={sentryRef} className="flex justify-center py-2">
            {isFetchingNextPage && <CgSpinner className="animate-spin" size={16} />}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
